import { useCallback, useMemo, useState } from 'react'; import { useTranslation } from 'react-i18next';
import { IconAddressBook } from '@tabler/icons-react';
import { CrudMode, CrudSchemaFields, type EnsoFormItem } from '@ohpen/enso-ui';
import { Tag, Form } from 'antd';

import { TabProps } from '../interfaces';
import { CrudFormCard } from './CrudFormCard';
import { AddressField } from './AddressField';
import { getHighlightProps } from '../utils';
import { readKnownQualifiedDomains, rememberQualifiedDomains } from '@pmp/components';
import { DEFAULT_TEXT_MAX_LENGTH_MEDIUM, textLimitProps } from '@pmp/utils';
import styles from '../styles.module.css';

export const ContactDetails: React.FC<TabProps> = ({ form, mode, fcaModifiedFields, removeFcaModifiedField, setDisableSave }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;
  const type = Form.useWatch(['registrationDetails', 'type'])

  const [knownDomains, setKnownDomains] = useState<string[]>(readKnownQualifiedDomains);

  const onQualifiedDomainsChange = useCallback((values: string[]) => {
    setKnownDomains(rememberQualifiedDomains(values));
  }, []);

  const isFcaRegistered = useMemo(() => type !== 'NOT_FCA_REGISTERED', [type]);

  const columns: EnsoFormItem[] = useMemo(
    () => [
      {
        valueType: 'group',
        columns: [
          {
            title: t('firms.labels.mobile'),
            dataIndex: ['contactDetails', 'mobilePhone'],
            valueType: 'text',
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_MEDIUM),
              className: getHighlightProps(['contactDetails', 'mobilePhone'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('contactDetails', 'mobilePhone'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('firms.labels.mobile')}
                  {isFcaRegistered && (
                    <Tag color="blue" className={styles.fcaTag}>
                      {t('firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('firms.labels.telephone'),
            dataIndex: ['contactDetails', 'workPhone'],
            valueType: 'text',
            fieldProps: textLimitProps(DEFAULT_TEXT_MAX_LENGTH_MEDIUM),
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('firms.labels.website'),
            dataIndex: ['contactDetails', 'website'],
            valueType: 'text',
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_MEDIUM),
              className: getHighlightProps(['contactDetails', 'website'], fcaModifiedFields),
              onBlur: () => {
                removeFcaModifiedField?.('contactDetails', 'website');
              },
            },
            formItemProps: {
              label: (
                <span>
                  {t('firms.labels.website')}
                  {isFcaRegistered && (
                    <Tag color="blue" className={styles.fcaTag}>
                      {t('firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('firms.labels.complaintsWebPage'),
            dataIndex: ['contactDetails', 'complaintsWebsite'],
            valueType: 'text',
            readonly,
            fieldProps: textLimitProps(DEFAULT_TEXT_MAX_LENGTH_MEDIUM),
            colProps: { span: 12 },
          },
          {
            title: t('firms.labels.email'),
            dataIndex: ['contactDetails', 'email'],
            valueType: 'text',
            readonly,
            fieldProps: textLimitProps(DEFAULT_TEXT_MAX_LENGTH_MEDIUM),
            formItemProps: {
              required: true,
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        title: t('firms.labels.qualifiedDomains'),
        dataIndex: ['contactDetails', 'qualifiedDomains'],
        valueType: 'select',
        readonly,
        readonlyDisplay: 'tag',
        fieldProps: {
          mode: 'tags',
          tagColor: 'blue',
          tokenSeparators: [','],
          notFoundContent: null,
          options: knownDomains.map(d => ({ label: d, value: d })),
          onChange: onQualifiedDomainsChange,
        },
        formItemProps: {
          tooltip: readonly ? null : t('firms.labels.qualifiedDomainsTooltip'),
        },
        colProps: { span: 12 },
      },
    ],
    [
      readonly,
      t,
      fcaModifiedFields,
      removeFcaModifiedField,
      isFcaRegistered,
      knownDomains,
      onQualifiedDomainsChange,
    ],
  );

  return (
    <CrudFormCard collapsible={readonly} icon={<IconAddressBook />} title={t('firms.labels.contactDetails')}>
      <CrudSchemaFields schema={columns} form={form} readonly={readonly} component={false} />
      <AddressField
        readonly={readonly}
        form={form}
        getAddress={() => form?.getFieldValue(['contactDetails', 'firmAddress'])}
        fcaModifiedFields={fcaModifiedFields}
        removeFcaModifiedField={removeFcaModifiedField}
        isFromFca={isFcaRegistered}
        namePrefix={['contactDetails', 'firmAddress']}
        titleKey="firms.labels.registeredOfficeAddress"
        fcaFieldKey="contactDetails"
        fcaFieldNestedKey="firmAddress"
        enableLookup
        lookupBasePath={['contactDetails', 'firmAddress']}
        onAddressApplied={() => setDisableSave?.(false)}
      />
    </CrudFormCard>
  );
};
